import { signal, effect, untracked } from '@preact/signals-core';
import {
  activeRoleSignal,
  allCustomersSignal,
  assetsListSignal,
  customersSignal,
  deviceModelsSignal,
  getAuthHeaders,
  refreshAssets
} from '../assets';
import { consumablesSignal, refreshConsumables } from '../device_models';
import { usersListSignal, refreshUsers } from '../users';
import { components } from '../../api/v1';
import { escapeHtml } from '../../utils/sanitize';
import { showToast } from '../../utils/toast';

type Ticket = components['schemas']['Ticket'];
type Customer = components['schemas']['Customer'];
type Asset = components['schemas']['Asset'];
type User = components['schemas']['User'];
type ServiceSession = components['schemas']['ServiceSession'];
type Consumable = components['schemas']['Consumable'];
type PartRecommendation = components['schemas']['PartRecommendation'];

type OwnershipType = 'owned' | 'rented';
type ApprovalFilter = 'awaiting' | 'ready' | 'installed' | 'rejected' | 'all';
type ApprovalAction = 'approve' | 'reject' | 'install';

interface TicketContext {
  ticket: Ticket;
  customer: Customer | null;
  asset: Asset | null;
  ownership: OwnershipType;
  sessions: ServiceSession[];
  sessionsById: Map<string, ServiceSession>;
}

interface BatchSummary {
  action: ApprovalAction;
  ids: string[];
  recommendationCount: number;
  ticketCount: number;
  partCount: number;
  ownedCount: number;
  rentedCount: number;
  totalCost: number;
  lineItems: Array<{
    ticketNumber: string;
    partName: string;
    quantity: number;
    totalCost: number;
  }>;
}

interface SignatureSummary {
  recommendation: PartRecommendation;
  managerEmail: string;
  adminEmail: string;
  managerAt: string;
  adminAt: string;
  managerHash: string;
  adminHash: string;
}

interface RecommendationRowMeta {
  actionButtonsHtml: string;
  approvalChainHtml: string;
  costHtml: string;
  requestedByHtml: string;
  requestedAtHtml: string;
  signatureHtml: string;
  statusBadgeHtml: string;
}

export const partsStatusFilterSignal = signal<ApprovalFilter>('awaiting');
export const approvalsLoadingSignal = signal<boolean>(false);
export const recommendationsSignal = signal<PartRecommendation[]>([]);
export const ticketSessionMapSignal = signal<Map<string, TicketContext>>(new Map());
export const approvalsCurrentPageSignal = signal<number>(1);
export const recommendationsInEditMappingMode = signal<Set<string>>(new Set());

const PAGE_SIZE = 10;

const tempMappings = new Map<string, string>();

let pendingRejectRecommendationId: string | null = null;
let pendingRejectRecommendationLabel = '';
let pendingRejectReason = '';
let pendingBatchAction: ApprovalAction | null = null;
let pendingBatchIds: string[] = [];
let pendingBatchSummary: BatchSummary | null = null;
let pendingSignatureSummary: SignatureSummary | null = null;

function isManagerOrAbove(role: string): boolean {
  return role === 'superadmin' || role === 'admin' || role === 'manager';
}

function isAdminOrAbove(role: string): boolean {
  return role === 'superadmin' || role === 'admin';
}

function formatDate(value: string | Date | undefined | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`;
}

function formatDateTime(value: string | Date | undefined | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-NP');
}

function formatCurrency(value: number): string {
  return `NRs. ${Math.round(value).toLocaleString('en-NP')}`;
}

function normalizeFilterStatus(status: PartRecommendation['status'], ownership: OwnershipType): ApprovalFilter {
  if (status === 'requested' || (status === 'manager_approved' && ownership === 'rented')) {
    return 'awaiting';
  }

  if ((status === 'manager_approved' && ownership === 'owned') || (status === 'admin_approved' && ownership === 'rented')) {
    return 'ready';
  }

  if (status === 'installed') {
    return 'installed';
  }

  if (status === 'manager_rejected' || status === 'admin_rejected') {
    return 'rejected';
  }

  return 'all';
}

function getConsumableById(consumableId: string): Consumable | null {
  const consumable = (consumablesSignal.value || []).find((item: Consumable) => item.id === consumableId);
  return consumable || null;
}

function resolveOwnership(context: TicketContext | null): OwnershipType {
  const isRentedMPS = context?.asset ? (context.asset.isMps !== false && context.asset.ownershipType === 'rented') : false;
  return isRentedMPS ? 'rented' : 'owned';
}

function getRequestingTechnician(context: TicketContext | null, recommendation: PartRecommendation): User | null {
  if (!context || !recommendation.sessionId) return null;
  const session = context.sessionsById.get(recommendation.sessionId);
  if (!session) return null;
  return (usersListSignal.value || []).find((user: User) => user.id === session.technicianId) || null;
}

function getRecommendationStatusMeta(
  recommendation: PartRecommendation,
  ownership: OwnershipType
): { badge: string; tone: 'attention' | 'info' | 'success' | 'critical'; chain: string; state: string } {
  if (recommendation.status === 'requested') {
    return {
      badge: 'Awaiting manager',
      tone: 'attention',
      chain: ownership === 'rented' ? 'Step 1/2: Manager approval' : 'Step 1/1: Manager approval',
      state: 'Requested'
    };
  }

  if (recommendation.status === 'manager_approved' && ownership === 'rented') {
    return {
      badge: 'Awaiting admin',
      tone: 'attention',
      chain: 'Step 2/2: Admin approval',
      state: 'Manager approved'
    };
  }

  if (recommendation.status === 'manager_approved') {
    return {
      badge: 'Manager approved',
      tone: 'info',
      chain: 'Ready for install',
      state: 'Manager approved'
    };
  }

  if (recommendation.status === 'admin_approved') {
    return {
      badge: 'Admin approved',
      tone: 'info',
      chain: 'Ready for install',
      state: 'Admin approved'
    };
  }

  if (recommendation.status === 'installed') {
    return {
      badge: 'Installed',
      tone: 'success',
      chain: 'Completed',
      state: 'Installed'
    };
  }

  if (recommendation.status === 'manager_rejected' || recommendation.status === 'admin_rejected') {
    return {
      badge: 'Rejected',
      tone: 'critical',
      chain: 'Stopped',
      state: 'Rejected'
    };
  }

  return {
    badge: recommendation.status,
    tone: 'info',
    chain: recommendation.status,
    state: recommendation.status
  };
}

function getRecommendationCost(recommendation: PartRecommendation): number {
  const consumable = getConsumableById(recommendation.consumableId || '');
  return (consumable?.cost || 0) * recommendation.quantity;
}

function getRecommendationUnitCost(recommendation: PartRecommendation): number {
  return getConsumableById(recommendation.consumableId || '')?.cost || 0;
}

function getRecommendationPartName(recommendation: PartRecommendation): string {
  if (recommendation.colloquialName) return recommendation.colloquialName;
  return getConsumableById(recommendation.consumableId || '')?.name || 'Unknown Part';
}

function getRecommendationRequestedBy(context: TicketContext | null, recommendation: PartRecommendation): string {
  const technician = getRequestingTechnician(context, recommendation);
  if (technician?.email) return technician.email;

  // Fallback to assigned technician of the ticket if session not found or direct
  if (context?.ticket?.assignedUserIds && context.ticket.assignedUserIds.length > 0) {
    const assignedTechs = (usersListSignal.value || [])
      .filter((user: User) => context.ticket.assignedUserIds?.includes(user.id) && user.role === 'technician');
    if (assignedTechs.length > 0) {
      return assignedTechs.map(t => t.email).join(', ');
    }

    const assignedUser = (usersListSignal.value || [])
      .find((user: User) => context.ticket.assignedUserIds?.includes(user.id));
    if (assignedUser?.email) return assignedUser.email;
  }

  return 'Technician unavailable';
}

function getRecommendationSignatureSummary(recommendation: PartRecommendation): SignatureSummary {
  const managerEmail = (usersListSignal.value || []).find((user: User) => user.id === recommendation.managerApprovedBy)?.email || '—';
  const adminEmail = (usersListSignal.value || []).find((user: User) => user.id === recommendation.adminApprovedBy)?.email || '—';

  return {
    recommendation,
    managerEmail,
    adminEmail,
    managerAt: formatDateTime(recommendation.managerApprovedAt),
    adminAt: formatDateTime(recommendation.adminApprovedAt),
    managerHash: recommendation.managerSignature ? `${recommendation.managerSignature.slice(0, 24)}…` : '—',
    adminHash: recommendation.adminSignature ? `${recommendation.adminSignature.slice(0, 24)}…` : '—'
  };
}

function clampCurrentPage(totalGroups: number): void {
  const totalPages = Math.max(1, Math.ceil(totalGroups / PAGE_SIZE));
  approvalsCurrentPageSignal.value = Math.min(Math.max(1, approvalsCurrentPageSignal.value), totalPages);
}

function updateRecommendationList(updated: PartRecommendation | PartRecommendation[]): void {
  const next = Array.isArray(updated) ? updated : [updated];
  const byId = new Map(next.map((item) => [item.id, item]));
  recommendationsSignal.value = (recommendationsSignal.value || []).map((item) => byId.get(item.id) || item);

  const currentGroups = getGroupedRecommendations(getFilteredRecommendations());
  clampCurrentPage(currentGroups.length);
}

function buildTicketContextMap(tickets: Ticket[]): Promise<Map<string, TicketContext>> {
  const customers = (allCustomersSignal.value && allCustomersSignal.value.length > 0)
    ? allCustomersSignal.value
    : (customersSignal.value || []);
  const assets = assetsListSignal.value || [];
  const headers = getAuthHeaders();

  return Promise.all(
    tickets.map(async (ticket) => {
      let customer: Customer | null = customers.find((item: Customer) => item.id === ticket.customerId) || null;
      let asset: Asset | null = assets.find((item: Asset) => item.id === ticket.assetId) || null;

      if (ticket.customerId && !customer) {
        try {
          const resC = await fetch(`/api/v1/customers/${ticket.customerId}`, { headers });
          if (resC.ok) customer = (await resC.json()) as Customer;
        } catch (e) {
          // ignore
        }
      }

      if (ticket.assetId && !asset) {
        try {
          const resA = await fetch(`/api/v1/assets/${ticket.assetId}`, { headers });
          if (resA.ok) asset = (await resA.json()) as Asset;
        } catch (e) {
          // ignore
        }
      }

      const context: TicketContext = {
        ticket,
        customer,
        asset,
        ownership: 'owned',
        sessions: [],
        sessionsById: new Map<string, ServiceSession>()
      };

      context.ownership = resolveOwnership(context);

      try {
        const res = await fetch(`/api/v1/tickets/${ticket.id}/sessions`, { headers });
        if (res.ok) {
          const sessions = (await res.json()) as ServiceSession[];
          context.sessions = sessions;
          context.sessionsById = new Map(sessions.map((session) => [session.id, session]));
        }
      } catch (error) {
        console.error(`Failed to fetch sessions for ticket ${ticket.id}:`, error);
      }

      return [ticket.id, context] as const;
    })
  ).then((entries) => new Map(entries));
}

export async function refreshPartsApprovalsDashboard(): Promise<void> {
  approvalsLoadingSignal.value = true;

  try {
    const headers = getAuthHeaders();

    const recRes = await fetch('/api/v1/parts-recommendations', { headers });
    if (!recRes.ok) {
      throw new Error('Failed to fetch recommendations');
    }

    const recs = (await recRes.json()) as PartRecommendation[];
    recommendationsSignal.value = recs;

    await Promise.all([
      refreshUsers(),
      refreshAssets(),
      refreshConsumables(),
      (async () => {
        if (!allCustomersSignal.value.length) {
          try {
            const res = await fetch('/api/v1/customers', { headers });
            if (res.ok) allCustomersSignal.value = await res.json();
          } catch (e) {
            // ignore
          }
        }
      })(),
      (async () => {
        if (!deviceModelsSignal.value.length) {
          try {
            const res = await fetch('/api/v1/device-models', { headers });
            if (res.ok) deviceModelsSignal.value = await res.json();
          } catch (e) {
            // ignore
          }
        }
      })()
    ]);

    const uniqueTicketIds = [...new Set(recs.map((rec) => rec.ticketId))];
    const tickets = (
      await Promise.all(
        uniqueTicketIds.map(async (ticketId) => {
          try {
            const res = await fetch(`/api/v1/tickets/${ticketId}`, { headers });
            if (!res.ok) return null;
            return (await res.json()) as Ticket;
          } catch (error) {
            console.error(`Failed to fetch ticket ${ticketId}:`, error);
            return null;
          }
        })
      )
    ).filter((ticket): ticket is Ticket => ticket !== null);

    const contextMap = await buildTicketContextMap(tickets);
    ticketSessionMapSignal.value = contextMap;

    clampCurrentPage(getGroupedRecommendations(getFilteredRecommendations()).length);
  } catch (error) {
    console.error('Failed to load Parts Approvals Dashboard:', error);
    showToast('Failed to load parts approvals data.', 'error');
  } finally {
    approvalsLoadingSignal.value = false;
  }
}

function getFilteredRecommendations(): PartRecommendation[] {
  const filter = partsStatusFilterSignal.value;
  const contexts = ticketSessionMapSignal.value;

  const filtered = (recommendationsSignal.value || []).filter((recommendation) => {
    const context = contexts.get(recommendation.ticketId) || null;
    const ownership = resolveOwnership(context);
    const bucket = normalizeFilterStatus(recommendation.status, ownership);

    if (filter === 'all') return true;
    return bucket === filter;
  });

  return sortRecommendations(filtered, contexts);
}

function sortRecommendations(
  recommendations: PartRecommendation[],
  contexts: Map<string, TicketContext>
): PartRecommendation[] {
  const statusOrder: Record<ApprovalFilter, number> = {
    awaiting: 0,
    ready: 1,
    installed: 2,
    rejected: 3,
    all: 4
  };

  return [...recommendations].sort((left, right) => {
    const leftContext = contexts.get(left.ticketId) || null;
    const rightContext = contexts.get(right.ticketId) || null;
    const leftOrder = statusOrder[normalizeFilterStatus(left.status, resolveOwnership(leftContext))];
    const rightOrder = statusOrder[normalizeFilterStatus(right.status, resolveOwnership(rightContext))];

    if (leftOrder !== rightOrder) return leftOrder - rightOrder;

    const leftDate = new Date(left.createdAt).getTime();
    const rightDate = new Date(right.createdAt).getTime();
    if (leftDate !== rightDate) return rightDate - leftDate;

    return getRecommendationPartName(left).localeCompare(getRecommendationPartName(right));
  });
}

function getGroupedRecommendations(recommendations: PartRecommendation[]): Array<{ ticket: Ticket; context: TicketContext | null; recommendations: PartRecommendation[] }> {
  const groups = new Map<string, { ticket: Ticket; context: TicketContext | null; recommendations: PartRecommendation[] }>();

  recommendations.forEach((recommendation) => {
    const context = ticketSessionMapSignal.value.get(recommendation.ticketId) || null;
    const existing = groups.get(recommendation.ticketId);

    if (!existing) {
      groups.set(recommendation.ticketId, {
        ticket: context?.ticket || {
          id: recommendation.ticketId,
          ticketNumber: 'Unknown',
          customerId: '',
          contactNumber: '',
          description: '',
          status: 'open',
          priority: 'medium',
          createdAt: recommendation.createdAt,
          updatedAt: recommendation.updatedAt
        },
        context,
        recommendations: [recommendation]
      });
      return;
    }

    existing.recommendations.push(recommendation);
  });

  return [...groups.values()].sort((left, right) => {
    const leftDate = new Date(left.ticket.createdAt).getTime();
    const rightDate = new Date(right.ticket.createdAt).getTime();
    if (leftDate !== rightDate) return rightDate - leftDate;
    return left.ticket.ticketNumber.localeCompare(right.ticket.ticketNumber);
  });
}

function getBatchSummary(ids: string[], action: ApprovalAction): BatchSummary {
  const recommendations = (recommendationsSignal.value || []).filter((item) => ids.includes(item.id));
  const ticketIds = [...new Set(recommendations.map((item) => item.ticketId))];
  const contexts = ticketSessionMapSignal.value;

  const summary: BatchSummary = {
    action,
    ids,
    recommendationCount: recommendations.length,
    ticketCount: ticketIds.length,
    partCount: recommendations.reduce((sum, item) => sum + item.quantity, 0),
    ownedCount: 0,
    rentedCount: 0,
    totalCost: 0,
    lineItems: []
  };

  recommendations.forEach((recommendation) => {
    const context = contexts.get(recommendation.ticketId) || null;
    if (resolveOwnership(context) === 'rented') {
      summary.rentedCount += 1;
    } else {
      summary.ownedCount += 1;
    }

    const unitCost = getRecommendationUnitCost(recommendation);
    const totalCost = unitCost * recommendation.quantity;
    summary.totalCost += totalCost;
    summary.lineItems.push({
      ticketNumber: context?.ticket.ticketNumber || recommendation.ticketId,
      partName: getRecommendationPartName(recommendation),
      quantity: recommendation.quantity,
      totalCost
    });
  });

  return summary;
}

let _savedPartsScrollY = 0;
let _savedPartsScrollX = 0;
let _savedPartsElementId: string | null = null;

export function savePartsDashboardScrollState(targetId?: string): void {
  _savedPartsScrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
  _savedPartsScrollX = window.scrollX || window.pageXOffset || document.documentElement.scrollLeft || 0;
  if (targetId) {
    _savedPartsElementId = targetId;
  }
}

export function restorePartsDashboardScrollState(): void {
  if (_savedPartsScrollY > 0 || _savedPartsScrollX > 0) {
    const scrollY = _savedPartsScrollY;
    const scrollX = _savedPartsScrollX;
    const targetId = _savedPartsElementId;

    requestAnimationFrame(() => {
      window.scrollTo({
        top: scrollY,
        left: scrollX,
        behavior: 'instant' as ScrollBehavior
      });

      if (targetId) {
        const el = document.getElementById(targetId) || document.querySelector(`[data-id="${targetId}"]`);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top < 0 || rect.bottom > window.innerHeight) {
            el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }
      }
    });
  }
}

function getActionUrl(recommendationId: string): string {
  return `/api/v1/parts-recommendations/${recommendationId}/approve`;
}

async function submitRecommendationAction(action: ApprovalAction, recommendationId: string, payload: { reason?: string; consumableId?: string } = {}): Promise<void> {
  savePartsDashboardScrollState(recommendationId);
  const button = document.querySelector(`[data-action="${action}"][data-id="${recommendationId}"]`) as HTMLButtonElement | null;
  if (button) {
    button.disabled = true;
    button.setAttribute('loading', 'true');
  }

  if (action === 'approve') {
    const el = document.querySelector(`.inline-consumable-mapper[data-recommendation-id="${recommendationId}"]`) as any;
    if (el) {
      const selectedId = el.selectedId;
      if (!selectedId) {
        showToast('Please select a catalog consumable to map before approving.', 'error');
        if (button) {
          button.disabled = false;
          button.removeAttribute('loading');
        }
        return;
      }
      payload.consumableId = selectedId;
    }
  }

  try {
    const res = await fetch(getActionUrl(recommendationId), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ action, ...payload })
    });

    if (!res.ok) {
      const errBody = await res.json().catch(() => ({ error: 'Action failed' }));
      showToast(errBody.error || 'Failed to complete approval action.', 'error');
      return;
    }

    const updated = (await res.json()) as PartRecommendation;
    tempMappings.delete(recommendationId);
    updateRecommendationList(updated);
    showToast(`Action "${action}" completed successfully.`, 'success');
    window.dispatchEvent(new CustomEvent('opm-state-change'));
  } catch (error) {
    console.error(`Approval action ${action} failed:`, error);
    showToast('Network error while completing approval action.', 'error');
  } finally {
    if (button) {
      button.disabled = false;
      button.removeAttribute('loading');
    }
  }
}

async function submitBatchAction(action: ApprovalAction, ids: string[], payload: { reason?: string } = {}): Promise<void> {
  savePartsDashboardScrollState(ids[0]);
  const button = document.querySelector(`[data-bulk-action="${action}"][data-ids="${ids.join(',')}"]`) as HTMLButtonElement | null;
  if (button) {
    button.disabled = true;
    button.setAttribute('loading', 'true');
  }

  try {
    if (action === 'approve') {
      const mapPromises: Promise<any>[] = [];
      ids.forEach((id) => {
        const tempId = tempMappings.get(id);
        if (tempId) {
          mapPromises.push(
            fetch(`/api/v1/parts-recommendations/${id}`, {
              method: 'PATCH',
              headers: getAuthHeaders(),
              body: JSON.stringify({ consumableId: tempId })
            })
          );
        }
      });

      if (mapPromises.length > 0) {
        const results = await Promise.all(mapPromises);
        const failed = results.find((r) => !r.ok);
        if (failed) {
          showToast('Failed to save some consumable mappings before batch approval.', 'error');
          if (button) {
            button.disabled = false;
            button.removeAttribute('loading');
          }
          return;
        }
      }
    }

    const res = await fetch('/api/v1/parts-recommendations/batch-action', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ recommendationIds: ids, action, ...payload })
    });

    if (!res.ok) {
      const errBody = await res.json().catch(() => ({ error: 'Batch action failed' }));
      showToast(errBody.error || 'Failed to complete batch action.', 'error');
      return;
    }

    const updated = (await res.json()) as PartRecommendation[];
    ids.forEach((id) => tempMappings.delete(id));
    updateRecommendationList(updated);
    showToast(`Batch "${action}" completed successfully for ${ids.length} items.`, 'success');
    window.dispatchEvent(new CustomEvent('opm-state-change'));
  } catch (error) {
    console.error(`Batch action ${action} failed:`, error);
    showToast('Network error while completing batch action.', 'error');
  } finally {
    if (button) {
      button.disabled = false;
      button.removeAttribute('loading');
    }
  }
}

function renderFilterTabs(): string {
  const counts = { awaiting: 0, ready: 0, installed: 0, rejected: 0, all: 0 };
  const contexts = ticketSessionMapSignal.value;

  (recommendationsSignal.value || []).forEach((recommendation) => {
    const context = contexts.get(recommendation.ticketId) || null;
    const bucket = normalizeFilterStatus(recommendation.status, resolveOwnership(context));
    counts.all += 1;
    if (bucket !== 'all') {
      counts[bucket] += 1;
    }
  });

  const tabs: Array<{ key: ApprovalFilter; label: string }> = [
    { key: 'awaiting', label: 'Awaiting' },
    { key: 'ready', label: 'Ready' },
    { key: 'installed', label: 'Installed' },
    { key: 'rejected', label: 'Rejected' },
    { key: 'all', label: 'All' }
  ];

  return `
    <div class="ticket-filter-bar parts-approvals-filter-bar">
      ${tabs.map((tab) => {
    const active = partsStatusFilterSignal.value === tab.key;
    return `
          <button
            type="button"
            class="ticket-filter-tab parts-approvals-filter-tab ${active ? 'ticket-filter-tab--active' : ''}"
            data-filter="${tab.key}"
            aria-pressed="${active ? 'true' : 'false'}"
          >
            ${escapeHtml(tab.label)}
            <span class="ticket-filter-count">${counts[tab.key]}</span>
          </button>
        `;
  }).join('')}
    </div>
  `;
}

function renderRowMeta(recommendation: PartRecommendation, context: TicketContext | null): RecommendationRowMeta {
  const ownership = resolveOwnership(context);
  const meta = getRecommendationStatusMeta(recommendation, ownership);
  const consumable = getConsumableById(recommendation.consumableId || '');
  const partName = escapeHtml(consumable?.name || 'Unknown Part');
  const unitCost = getRecommendationUnitCost(recommendation);
  const totalCost = getRecommendationCost(recommendation);
  const requestedBy = escapeHtml(getRecommendationRequestedBy(context, recommendation));
  const requestedAt = escapeHtml(formatDate(recommendation.createdAt));
  const statusBadgeHtml = `<s-badge tone="${meta.tone}">${escapeHtml(meta.badge)}</s-badge>`;
  const approvalChainHtml = `<s-badge tone="info">${escapeHtml(meta.chain)}</s-badge>`;
  const costHtml = `
    <s-stack gap="tight">
      <s-text>${partName}</s-text>
      <s-text tone="subdued">NRs. ${escapeHtml(unitCost.toLocaleString('en-NP'))} each</s-text>
      <s-text tone="subdued">Total: ${escapeHtml(formatCurrency(totalCost))}</s-text>
    </s-stack>
  `;

  const actions = renderActions(recommendation, ownership, meta);
  const signature = renderSignatureCell(recommendation);

  return {
    actionButtonsHtml: actions,
    approvalChainHtml,
    costHtml,
    requestedByHtml: requestedBy,
    requestedAtHtml: requestedAt,
    signatureHtml: signature,
    statusBadgeHtml
  };
}

function renderActions(recommendation: PartRecommendation, ownership: OwnershipType, meta: { badge: string; tone: 'attention' | 'info' | 'success' | 'critical'; chain: string; state: string }): string {
  const role = activeRoleSignal.value;
  const canManagerAction = isManagerOrAbove(role);
  const canAdminAction = isAdminOrAbove(role);

  if (recommendation.status === 'requested' && canManagerAction) {
    return `
      <s-stack direction="row" gap="tight" style="flex-wrap:wrap;">
        <s-button size="slim" variant="primary" data-action="approve" data-id="${recommendation.id}">Approve</s-button>
        <s-button size="slim" variant="critical" data-action="reject" data-id="${recommendation.id}">Reject</s-button>
      </s-stack>
    `;
  }

  if (recommendation.status === 'manager_approved' && ownership === 'rented' && canAdminAction) {
    return `
      <s-stack direction="row" gap="tight" style="flex-wrap:wrap;">
        <s-button size="slim" variant="primary" data-action="approve" data-id="${recommendation.id}">Admin Approve</s-button>
        <s-button size="slim" variant="critical" data-action="reject" data-id="${recommendation.id}">Admin Reject</s-button>
      </s-stack>
    `;
  }

  if ((recommendation.status === 'manager_approved' && ownership === 'owned' && canManagerAction) || (recommendation.status === 'admin_approved' && ownership === 'rented' && canManagerAction)) {
    return `<s-button size="slim" variant="primary" data-action="install" data-id="${recommendation.id}">Install</s-button>`;
  }

  if (meta.state === 'Rejected') {
    return '—';
  }

  return '—';
}

function renderSignatureCell(recommendation: PartRecommendation): string {
  const hasManagerSignature = Boolean(recommendation.managerSignature);
  const hasAdminSignature = Boolean(recommendation.adminSignature);

  if (!hasManagerSignature && !hasAdminSignature) return '—';

  return `
    <s-stack gap="tight">
      <s-stack direction="row" gap="tight" style="flex-wrap:wrap;">
        ${hasManagerSignature ? '<s-badge tone="success">Manager signed</s-badge>' : ''}
        ${hasAdminSignature ? '<s-badge tone="success">Admin signed</s-badge>' : ''}
      </s-stack>
      <s-button size="slim" variant="secondary" data-signature-id="${recommendation.id}">Review signatures</s-button>
    </s-stack>
  `;
}

function renderRecommendationRows(recommendations: PartRecommendation[], context: TicketContext | null): string {
  const role = activeRoleSignal.value;
  const isEditMode = (id: string) => recommendationsInEditMappingMode.value.has(id);

  return recommendations
    .map((recommendation) => {
      const meta = renderRowMeta(recommendation, context);
      const partName = escapeHtml(getRecommendationPartName(recommendation));
      const hasConsumable = !!recommendation.consumableId;
      const showMapper = !hasConsumable || isEditMode(recommendation.id);

      return `
        <tr style="background: var(--p-color-bg-surface, #ffffff); border-bottom: 1px solid var(--p-color-border-subdued, #e1e3e5); transition: background 0.15s ease;">
          <td style="padding:0.75rem; border-bottom:1px solid var(--p-color-border-subdued, #e1e3e5);">
            <s-stack gap="tight">
              <s-text>${partName}</s-text>
              ${showMapper ? `
                <div style="display:flex; align-items:center; gap:0.5rem; min-height:40px;">
                  <div style="width:220px;">
                    <opm-autocomplete
                      class="inline-consumable-mapper"
                      data-recommendation-id="${recommendation.id}"
                      floating-dropdown
                      label=""
                      placeholder="Map to catalog part…"
                      selected-id="${recommendation.consumableId || tempMappings.get(recommendation.id) || ''}"
                    ></opm-autocomplete>
                  </div>
                  ${hasConsumable ? `
                    <s-button size="slim" variant="plain" class="btn-cancel-edit-mapping" data-recommendation-id="${recommendation.id}">
                      Cancel
                    </s-button>
                  ` : ''}
                </div>
              ` : `
                <div style="display:flex; align-items:center; gap:0.5rem; flex-wrap:wrap;">
                  <s-text tone="subdued">${escapeHtml(getConsumableById(recommendation.consumableId!)?.code || 'No code')}${getRecommendationCost(recommendation) > 0 ? ` · ${formatCurrency(getRecommendationCost(recommendation))}` : ''}</s-text>
                  ${isManagerOrAbove(role) && recommendation.status !== 'installed' ? `
                    <s-button size="slim" variant="plain" class="btn-edit-mapping" data-recommendation-id="${recommendation.id}">
                      Edit Mapping
                    </s-button>
                  ` : ''}
                </div>
              `}
            </s-stack>
          </td>
          <td style="padding:0.75rem; border-bottom:1px solid var(--p-color-border-subdued, #e1e3e5); text-align:center;">${recommendation.quantity}</td>
          <td style="padding:0.75rem; border-bottom:1px solid var(--p-color-border-subdued, #e1e3e5);">${meta.requestedByHtml}</td>
          <td style="padding:0.75rem; border-bottom:1px solid var(--p-color-border-subdued, #e1e3e5);">${meta.requestedAtHtml}</td>
          <td style="padding:0.75rem; border-bottom:1px solid var(--p-color-border-subdued, #e1e3e5);">${meta.approvalChainHtml}</td>
          <td style="padding:0.75rem; border-bottom:1px solid var(--p-color-border-subdued, #e1e3e5);">${meta.statusBadgeHtml}</td>
          <td style="padding:0.75rem; border-bottom:1px solid var(--p-color-border-subdued, #e1e3e5);">${meta.signatureHtml}</td>
          <td style="padding:0.75rem; border-bottom:1px solid var(--p-color-border-subdued, #e1e3e5);">${meta.actionButtonsHtml}</td>
        </tr>
      `;
    })
    .join('');
}

function renderGroupHeader(ticket: Ticket, context: TicketContext | null, recommendations: PartRecommendation[]): string {
  const totalQuantity = recommendations.reduce((sum, item) => sum + item.quantity, 0);

  const customerName = escapeHtml(
    context?.customer?.name ||
    (allCustomersSignal.value || []).find((c: Customer) => c.id === ticket.customerId)?.name ||
    (customersSignal.value || []).find((c: Customer) => c.id === ticket.customerId)?.name ||
    (ticket as any).customerNameRaw ||
    '—'
  );

  let modelNameFromAsset = '';
  if (context?.asset) {
    const model = (deviceModelsSignal.value || []).find((m: any) => m.id === context.asset?.modelId);
    if (model) {
      modelNameFromAsset = `${model.vendor} ${model.modelName}`.trim();
    } else if ((context.asset as any).modelName) {
      modelNameFromAsset = (context.asset as any).modelName;
    } else if ((context.asset as any).model) {
      modelNameFromAsset = (context.asset as any).model;
    }
  }

  const modelName = escapeHtml(modelNameFromAsset || ticket.modelNameRaw || 'Linked asset');
  const serialNumber = escapeHtml(context?.asset?.serialNumber || ticket.serialNumberRaw || '—');

  const isMps = context?.asset ? context.asset.isMps !== false : true;

  return `
    <s-stack gap="tight">
      <s-stack direction="row" gap="tight" align="center" style="flex-wrap:wrap; justify-content:space-between;">
        <s-stack gap="tight">
          <s-heading element="h3" style="margin:0;">
            <a href="#ticket-console?id=${ticket.id}" style="color:var(--p-color-text-link, #008060); text-decoration:none;" onmouseover="this.style.textDecoration='underline'" onmouseout="this.style.textDecoration='none'">
              Ticket ${escapeHtml(ticket.ticketNumber)}
            </a>
          </s-heading>
          <s-text tone="subdued">Customer: <strong>${customerName}</strong> · Asset: <strong>${modelName}</strong> · SN: <strong>${serialNumber}</strong></s-text>
        </s-stack>
        <s-stack direction="row" gap="tight" style="flex-wrap:wrap; justify-content:flex-end;">
          <s-badge tone="${isMps ? 'info' : 'attention'}">${isMps ? 'MPS' : 'Non-MPS'}</s-badge>
          <s-badge tone="info">${recommendations.length} requests</s-badge>
          <s-badge tone="success">${totalQuantity} parts</s-badge>
        </s-stack>
      </s-stack>
    </s-stack>
  `;
}

function renderBatchActions(recommendations: PartRecommendation[], context: TicketContext | null): string {
  const role = activeRoleSignal.value;
  const canManagerAction = isManagerOrAbove(role);
  if (!canManagerAction) return '';

  const ownership = resolveOwnership(context);
  const approveIds: string[] = [];
  const installIds: string[] = [];

  recommendations.forEach((recommendation) => {
    if (recommendation.status === 'requested') {
      approveIds.push(recommendation.id);
      return;
    }

    if (recommendation.status === 'manager_approved' && ownership === 'rented') {
      approveIds.push(recommendation.id);
      return;
    }

    if ((recommendation.status === 'manager_approved' && ownership === 'owned') || (recommendation.status === 'admin_approved' && ownership === 'rented')) {
      installIds.push(recommendation.id);
    }
  });

  const buttons: string[] = [];

  if (approveIds.length > 1) {
    buttons.push(`<s-button size="slim" variant="primary" data-bulk-action="approve" data-ids="${approveIds.join(',')}">Approve All (${approveIds.length})</s-button>`);
  }

  if (installIds.length > 1) {
    buttons.push(`<s-button size="slim" variant="primary" data-bulk-action="install" data-ids="${installIds.join(',')}">Install All (${installIds.length})</s-button>`);
  }

  if (buttons.length === 0) return '';

  return `
    <s-stack direction="row" gap="tight" style="justify-content:flex-end; flex-wrap:wrap;">
      ${buttons.join('')}
    </s-stack>
  `;
}

function renderRecommendationGroup(group: { ticket: Ticket; context: TicketContext | null; recommendations: PartRecommendation[] }): string {
  const ticketRecommendations = [...group.recommendations];
  const ownership = resolveOwnership(group.context);
  const context = group.context;
  const approvedFirst = ticketRecommendations.sort((left, right) => {
    const leftWeight = normalizeFilterStatus(left.status, ownership);
    const rightWeight = normalizeFilterStatus(right.status, ownership);
    const order: Record<ApprovalFilter, number> = {
      awaiting: 0,
      ready: 1,
      installed: 2,
      rejected: 3,
      all: 4
    };

    if (order[leftWeight] !== order[rightWeight]) return order[leftWeight] - order[rightWeight];

    return new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime();
  });

  return `
    <s-box padding="base" background="surface" border="base" borderRadius="base">
      <s-stack gap="base">
        ${renderGroupHeader(group.ticket, context, ticketRecommendations)}

        <div style="overflow-x:auto;">
          <table style="width:100%; border-collapse:collapse; min-width:1100px;">
            <thead>
              <tr style="background:var(--p-color-bg-surface-secondary, #f1f2f4);">
                <th style="padding:0.75rem; text-align:left;">Part</th>
                <th style="padding:0.75rem; text-align:center;">Qty</th>
                <th style="padding:0.75rem; text-align:left;">Requested By</th>
                <th style="padding:0.75rem; text-align:left;">Requested At</th>
                <th style="padding:0.75rem; text-align:left;">Workflow</th>
                <th style="padding:0.75rem; text-align:left;">Status</th>
                <th style="padding:0.75rem; text-align:left;">Signatures</th>
                <th style="padding:0.75rem; text-align:left; width:220px;">Actions</th>
              </tr>
            </thead>
            <tbody>
              ${renderRecommendationRows(approvedFirst, context)}
            </tbody>
          </table>
        </div>

        ${renderBatchActions(ticketRecommendations, context)}
      </s-stack>
    </s-box>
  `;
}

function renderActionReviewModal(): string {
  return `
    <s-modal id="reject-recommendation-modal" heading="Reject Part Request">
      <s-stack gap="base" style="padding:1rem;">
        <s-banner tone="critical">
          Please explain why this request is being rejected. The reason is submitted with the action for audit visibility.
        </s-banner>
        <s-text id="reject-recommendation-title" tone="subdued"></s-text>
        <s-text-area
          id="reject-reason-input"
          label="Rejection reason"
          name="rejectReason"
          placeholder="Explain why this recommendation is being rejected..."
        ></s-text-area>
        <s-stack direction="row" gap="tight" style="justify-content:flex-end; flex-wrap:wrap;">
          <s-button commandFor="reject-recommendation-modal" command="--hide">Keep Request</s-button>
          <s-button id="btn-submit-reject" variant="critical" disabled>Reject Request</s-button>
        </s-stack>
      </s-stack>
    </s-modal>

    <s-modal id="batch-review-modal" heading="Review Batch Action">
      <s-stack gap="base" style="padding:1rem;">
        <s-banner tone="info">
          Review the summary below before committing this bulk workflow change.
        </s-banner>
        <div id="batch-review-summary"></div>
        <s-stack direction="row" gap="tight" style="justify-content:flex-end; flex-wrap:wrap;">
          <s-button commandFor="batch-review-modal" command="--hide">Cancel</s-button>
          <s-button id="btn-submit-batch-action" variant="primary">Confirm Batch Action</s-button>
        </s-stack>
      </s-stack>
    </s-modal>

    <s-modal id="signature-review-modal" heading="Signature Details">
      <s-stack gap="base" style="padding:1rem;">
        <s-banner tone="info">
          Signature metadata is shown here so touch users can inspect the approval chain without hover-only tooltips.
        </s-banner>
        <div id="signature-review-summary"></div>
        <s-button commandFor="signature-review-modal" command="--hide">Close</s-button>
      </s-stack>
    </s-modal>
  `;
}

function renderBatchSummary(summary: BatchSummary): string {
  const lineItems = summary.lineItems
    .map(
      (item) => `
        <div style="display:flex; justify-content:space-between; gap:0.75rem; padding:0.5rem 0; border-bottom:1px solid var(--p-color-border-subdued, #e1e3e5);">
          <span><strong>${escapeHtml(item.ticketNumber)}</strong> · ${escapeHtml(item.partName)} × ${item.quantity}</span>
          <span>${escapeHtml(formatCurrency(item.totalCost))}</span>
        </div>
      `
    )
    .join('');

  return `
    <s-stack gap="base">
      <s-stack direction="row" gap="tight" style="flex-wrap:wrap;">
        <s-badge tone="info">${summary.recommendationCount} recommendations</s-badge>
        <s-badge tone="info">${summary.ticketCount} tickets</s-badge>
        <s-badge tone="success">${summary.partCount} parts</s-badge>
        <s-badge tone="${summary.rentedCount > 0 ? 'attention' : 'info'}">${summary.ownedCount} owned / ${summary.rentedCount} rented</s-badge>
        <s-badge tone="success">${escapeHtml(formatCurrency(summary.totalCost))}</s-badge>
      </s-stack>
      <s-box padding="base" background="surface-secondary" border="base" borderRadius="base">
        ${lineItems || '<s-text tone="subdued">No recommendation rows were found for this action.</s-text>'}
      </s-box>
    </s-stack>
  `;
}

function renderSignatureSummary(summary: SignatureSummary): string {
  const recommendation = summary.recommendation;
  return `
    <s-stack gap="base">
      <s-banner tone="info">
        Manager and admin signatures are shown together so the approver can review the entire chain on touch devices.
      </s-banner>
      <s-box padding="base" background="surface-secondary" border="base" borderRadius="base">
        <s-stack gap="tight">
          <s-text><strong>Ticket:</strong> ${escapeHtml(recommendation.ticketId)}</s-text>
          <s-text><strong>Part:</strong> ${escapeHtml(getRecommendationPartName(recommendation))}</s-text>
          <s-text><strong>Manager:</strong> ${escapeHtml(summary.managerEmail)} · ${escapeHtml(summary.managerAt)}</s-text>
          <s-text><strong>Manager hash:</strong> ${escapeHtml(summary.managerHash)}</s-text>
          <s-text><strong>Admin:</strong> ${escapeHtml(summary.adminEmail)} · ${escapeHtml(summary.adminAt)}</s-text>
          <s-text><strong>Admin hash:</strong> ${escapeHtml(summary.adminHash)}</s-text>
        </s-stack>
      </s-box>
    </s-stack>
  `;
}

function renderPartsApprovalsShellStyles(): string {
  return `
    <style>
      .parts-approvals-shell {
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100%;
        min-height: 0;
        overflow: hidden;
        background: var(--p-color-bg-app, #f6f6f7);
      }

      .parts-approvals-panel {
        display: flex;
        flex-direction: column;
        flex: 1;
        width: 100%;
        min-height: 0;
        overflow: hidden;
        background: var(--p-color-bg-surface, #ffffff);
      }

      .ticket-list-header {
        padding: 1.25rem 1.25rem 0.75rem;
        border-bottom: 1px solid var(--p-color-border-subdued, #e1e3e5);
        background: var(--p-color-bg-surface, #ffffff);
      }

      .ticket-filter-bar {
        display: flex;
        gap: 0.25rem;
        flex-wrap: wrap;
        padding: 0.75rem 1.25rem;
        background: var(--p-color-bg-surface, #ffffff);
        border-bottom: 1px solid var(--p-color-border-subdued, #e1e3e5);
      }

      .ticket-filter-tab {
        display: inline-flex;
        align-items: center;
        gap: 0.35rem;
        min-height: 30px;
        padding: 0.3rem 0.65rem;
        font-size: 0.8rem;
        font-weight: 500;
        border: 1px solid var(--p-color-border-subdued, #c4cdd5);
        border-radius: var(--p-border-radius-200, 8px);
        background: transparent;
        color: var(--p-color-text-subdued, #6d7175);
        cursor: pointer;
        transition: all 0.15s ease;
        font-family: inherit;
      }

      .ticket-filter-tab:hover {
        background: var(--p-color-bg-action-hover, rgba(0,0,0,0.05));
        color: var(--p-color-text, #202223);
      }

      .ticket-filter-tab--active {
        background: var(--p-color-bg-surface-selected, #e4e6eb);
        color: var(--p-color-text, #202223);
        border-color: var(--p-color-border-accent, #008060);
        font-weight: 600;
      }

      .ticket-filter-count {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 18px;
        height: 18px;
        font-size: 0.7rem;
        font-weight: 600;
        background: var(--p-color-bg-surface-secondary, #f1f2f4);
        border-radius: 9px;
        padding: 0 4px;
      }

      .ticket-filter-tab--active .ticket-filter-count {
        background: var(--p-color-bg-action-primary, #008060);
        color: white;
      }

      .parts-approvals-body,
      .parts-approvals-loading {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        background: var(--p-color-bg-app, #f6f6f7);
      }

      .parts-approvals-loading {
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .parts-approvals-content {
        padding: 1rem 1.25rem 1.5rem;
        width: 100%;
        box-sizing: border-box;
      }

      .parts-approvals-content > * {
        width: 100%;
      }

      @media (max-width: 768px) {
        .ticket-list-header,
        .ticket-filter-bar,
        .parts-approvals-content {
          padding-left: 1rem;
          padding-right: 1rem;
        }
      }

      /* Elevate active table row and its cells during autocomplete focus to ensure dropdowns stack on top */
      tbody tr:focus-within {
        position: relative;
        z-index: 1000 !important;
      }
      tbody tr:focus-within td {
        position: relative;
        z-index: 1000 !important;
      }
    </style>
  `;
}

export function renderPartsApprovalsDashboard(): string {
  if (approvalsLoadingSignal.value) {
    return `
      ${renderPartsApprovalsShellStyles()}
      <div class="parts-approvals-shell">
        <div class="parts-approvals-panel">
          <div class="ticket-list-header parts-approvals-header">
            <s-stack gap="tight">
              <s-stack direction="row" gap="tight" align="center" style="justify-content:space-between; flex-wrap:wrap;">
                <s-heading element="h1" style="margin:0;">Parts Dispatch & Approvals Hub</s-heading>
                <s-badge tone="info">Loading</s-badge>
              </s-stack>
              <s-text tone="subdued">Review requests, signatures, and lifecycle state without losing the current page position.</s-text>
            </s-stack>
          </div>
          <div class="parts-approvals-loading">
            <s-stack gap="base" align="center" style="padding:4rem 0;">
              <s-spinner size="large"></s-spinner>
              <s-text tone="subdued">Loading approvals pipeline…</s-text>
            </s-stack>
          </div>
        </div>
      </div>
    `;
  }

  const filtered = getFilteredRecommendations();
  const grouped = getGroupedRecommendations(filtered);

  const currentPage = approvalsCurrentPageSignal.value;
  const totalPages = Math.max(1, Math.ceil(grouped.length / PAGE_SIZE));
  const safeCurrentPage = Math.min(Math.max(1, currentPage), totalPages);
  const paginated = grouped.slice((safeCurrentPage - 1) * PAGE_SIZE, safeCurrentPage * PAGE_SIZE);

  const cardsHtml = paginated.length
    ? paginated.map((group) => renderRecommendationGroup(group)).join('')
    : `
      <s-box padding="base" background="surface" border="base" borderRadius="base">
        <s-stack gap="tight" align="center" style="padding:2rem 0;">
          <s-badge tone="info">No matching recommendations</s-badge>
        </s-stack>
      </s-box>
    `;

  const paginationHtml = totalPages > 1
    ? `
      <s-stack direction="row" gap="base" align="center" style="justify-content:center; padding:0.5rem 0 1rem; flex-wrap:wrap;">
        <s-button id="btn-prev-page" ${safeCurrentPage === 1 ? 'disabled' : ''}>Previous</s-button>
        <s-text tone="subdued">Page <strong>${safeCurrentPage}</strong> of <strong>${totalPages}</strong></s-text>
        <s-button id="btn-next-page" ${safeCurrentPage === totalPages ? 'disabled' : ''}>Next</s-button>
      </s-stack>
    `
    : '';

  return `
    ${renderPartsApprovalsShellStyles()}

    <div class="parts-approvals-shell">
      <div class="parts-approvals-panel">
        <div class="ticket-list-header parts-approvals-header">
          <s-stack gap="tight">
            <s-stack direction="row" gap="tight" align="center" style="justify-content:space-between; flex-wrap:wrap;">
              <s-heading element="h1" style="margin:0;">Parts Approvals Hub</s-heading>
       
            </s-stack>
          </s-stack>
        </div>

        ${renderFilterTabs()}

        <div class="parts-approvals-body">
          <s-stack gap="base" class="parts-approvals-content">
            ${cardsHtml}
            ${paginationHtml}
          </s-stack>
        </div>
      </div>
    </div>

    ${renderActionReviewModal()}
  `;
}

function openRejectModal(recommendationId: string): void {
  const recommendation = (recommendationsSignal.value || []).find((item) => item.id === recommendationId) || null;
  if (!recommendation) return;

  pendingRejectRecommendationId = recommendationId;
  pendingRejectRecommendationLabel = `${getRecommendationPartName(recommendation)} · ${recommendationId}`;
  pendingRejectReason = '';

  const title = document.getElementById('reject-recommendation-title');
  if (title) {
    title.textContent = `Rejecting ${pendingRejectRecommendationLabel}`;
  }

  const input = document.getElementById('reject-reason-input') as HTMLTextAreaElement | null;
  if (input) {
    input.value = '';
  }

  const submit = document.getElementById('btn-submit-reject') as HTMLButtonElement | null;
  if (submit) {
    submit.disabled = true;
  }

  const modal = document.getElementById('reject-recommendation-modal') as HTMLElement & { showOverlay?: () => void };
  if (modal?.showOverlay) {
    modal.showOverlay();
  }
}

function openBatchModal(action: ApprovalAction, ids: string[]): void {
  if (action === 'approve') {
    const hasUnmapped = ids.some((id) => {
      const rec = (recommendationsSignal.value || []).find((r) => r.id === id);
      const isMapped = (rec && rec.consumableId) || tempMappings.get(id);
      return !isMapped;
    });
    if (hasUnmapped) {
      showToast('Some selected requests are colloquial suggestions. Please map them before batch approval.', 'warning');
      return;
    }
  }

  pendingBatchAction = action;
  pendingBatchIds = ids;
  pendingBatchSummary = getBatchSummary(ids, action);

  const body = document.getElementById('batch-review-summary');
  if (body && pendingBatchSummary) {
    body.innerHTML = renderBatchSummary(pendingBatchSummary);
  }

  const modal = document.getElementById('batch-review-modal') as HTMLElement & { showOverlay?: () => void };
  if (modal?.showOverlay) {
    modal.showOverlay();
  }
}

function openSignatureModal(recommendationId: string): void {
  const recommendation = (recommendationsSignal.value || []).find((item) => item.id === recommendationId) || null;
  if (!recommendation) return;

  pendingSignatureSummary = getRecommendationSignatureSummary(recommendation);

  const body = document.getElementById('signature-review-summary');
  if (body && pendingSignatureSummary) {
    body.innerHTML = renderSignatureSummary(pendingSignatureSummary);
  }

  const modal = document.getElementById('signature-review-modal') as HTMLElement & { showOverlay?: () => void };
  if (modal?.showOverlay) {
    modal.showOverlay();
  }
}

function bindRejectModalListeners(): void {
  const reasonInput = document.getElementById('reject-reason-input') as HTMLTextAreaElement | null;
  const submit = document.getElementById('btn-submit-reject') as HTMLButtonElement | null;

  if (reasonInput && submit) {
    reasonInput.addEventListener('input', () => {
      pendingRejectReason = reasonInput.value.trim();
      submit.disabled = pendingRejectReason.length === 0;
    });
  }

  if (submit) {
    submit.addEventListener('click', async () => {
      if (!pendingRejectRecommendationId || pendingRejectReason.trim().length === 0) return;
      submit.disabled = true;
      submit.setAttribute('loading', 'true');

      await submitRecommendationAction('reject', pendingRejectRecommendationId, { reason: pendingRejectReason.trim() });

      const modal = document.getElementById('reject-recommendation-modal') as HTMLElement & { hideOverlay?: () => void };
      if (modal?.hideOverlay) {
        modal.hideOverlay();
      }

      pendingRejectRecommendationId = null;
      pendingRejectRecommendationLabel = '';
      pendingRejectReason = '';
      submit.disabled = false;
      submit.removeAttribute('loading');
    });
  }
}

function bindBatchModalListeners(): void {
  const submit = document.getElementById('btn-submit-batch-action') as HTMLButtonElement | null;
  if (!submit) return;

  submit.addEventListener('click', async () => {
    if (!pendingBatchAction || pendingBatchIds.length === 0) return;

    submit.disabled = true;
    submit.setAttribute('loading', 'true');

    await submitBatchAction(pendingBatchAction, pendingBatchIds);

    const modal = document.getElementById('batch-review-modal') as HTMLElement & { hideOverlay?: () => void };
    if (modal?.hideOverlay) {
      modal.hideOverlay();
    }

    pendingBatchAction = null;
    pendingBatchIds = [];
    pendingBatchSummary = null;
    submit.disabled = false;
    submit.removeAttribute('loading');
  });
}

function bindFilterListeners(): void {
  const tabs = document.querySelectorAll('.parts-approvals-filter-tab[data-filter]');
  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      const filter = (tab as HTMLElement).dataset.filter as ApprovalFilter | undefined;
      if (!filter) return;

      partsStatusFilterSignal.value = filter;
      approvalsCurrentPageSignal.value = 1;
      clampCurrentPage(getGroupedRecommendations(getFilteredRecommendations()).length);
      window.dispatchEvent(new CustomEvent('opm-state-change'));
    });
  });
}

function bindPageListeners(): void {
  const prev = document.getElementById('btn-prev-page');
  const next = document.getElementById('btn-next-page');

  if (prev) {
    prev.addEventListener('click', () => {
      if (approvalsCurrentPageSignal.value > 1) {
        approvalsCurrentPageSignal.value -= 1;
        window.dispatchEvent(new CustomEvent('opm-state-change'));
      }
    });
  }

  if (next) {
    next.addEventListener('click', () => {
      const totalGroups = getGroupedRecommendations(getFilteredRecommendations()).length;
      const totalPages = Math.max(1, Math.ceil(totalGroups / PAGE_SIZE));
      if (approvalsCurrentPageSignal.value < totalPages) {
        approvalsCurrentPageSignal.value += 1;
        window.dispatchEvent(new CustomEvent('opm-state-change'));
      }
    });
  }
}

function bindActionButtons(): void {
  const actions = document.querySelectorAll('[data-action][data-id]');
  actions.forEach((element) => {
    element.addEventListener('click', (event) => {
      const target = event.currentTarget as HTMLElement;
      const action = target.dataset.action as ApprovalAction | undefined;
      const recommendationId = target.dataset.id;

      if (!action || !recommendationId) return;

      if (action === 'reject') {
        openRejectModal(recommendationId);
        return;
      }

      const actionType = action as ApprovalAction;
      void submitRecommendationAction(actionType, recommendationId);
    });
  });
}

function bindBatchButtons(): void {
  const buttons = document.querySelectorAll('[data-bulk-action][data-ids]');
  buttons.forEach((element) => {
    element.addEventListener('click', (event) => {
      const target = event.currentTarget as HTMLElement;
      const action = target.dataset.bulkAction as ApprovalAction | undefined;
      const idsValue = target.dataset.ids || '';

      if (!action || !idsValue) return;

      openBatchModal(action, idsValue.split(',').filter((value) => value.length > 0));
    });
  });
}

function bindSignatureButtons(): void {
  const buttons = document.querySelectorAll('[data-signature-id]');
  buttons.forEach((element) => {
    element.addEventListener('click', (event) => {
      const target = event.currentTarget as HTMLElement;
      const recommendationId = target.dataset.signatureId;
      if (!recommendationId) return;
      openSignatureModal(recommendationId);
    });
  });
}

const PARTS_FILTER_TAB_ORDER: ApprovalFilter[] = ['awaiting', 'ready', 'installed', 'rejected', 'all'];

let _partsKeyDownHandler: ((e: KeyboardEvent) => void) | null = null;

function switchPartsFilterTab(filter: ApprovalFilter): void {
  partsStatusFilterSignal.value = filter;
  approvalsCurrentPageSignal.value = 1;
  clampCurrentPage(getGroupedRecommendations(getFilteredRecommendations()).length);
  window.dispatchEvent(new CustomEvent('opm-state-change'));
}

function bindKeyboardListeners(): void {
  if (_partsKeyDownHandler) {
    window.removeEventListener('keydown', _partsKeyDownHandler);
    _partsKeyDownHandler = null;
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    const activeEl = document.activeElement;
    if (
      activeEl &&
      (activeEl.tagName === 'INPUT' ||
        activeEl.tagName === 'TEXTAREA' ||
        activeEl.tagName === 'SELECT' ||
        activeEl.closest('s-text-field') ||
        activeEl.closest('s-text-area') ||
        activeEl.closest('opm-autocomplete'))
    ) {
      if (e.key === 'Escape') {
        const modals = ['reject-reason-modal', 'batch-action-modal', 'signature-summary-modal'];
        modals.forEach(id => {
          const m = document.getElementById(id) as any;
          if (m && typeof m.hideOverlay === 'function' && m.hasAttribute('open')) {
            m.hideOverlay();
          }
        });
      }
      return;
    }

    if (e.key === 'Escape') {
      const modals = ['reject-reason-modal', 'batch-action-modal', 'signature-summary-modal'];
      modals.forEach(id => {
        const m = document.getElementById(id) as any;
        if (m && typeof m.hideOverlay === 'function' && m.hasAttribute('open')) {
          m.hideOverlay();
        }
      });
    }

    if (e.key === 'h' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const currentFilter = partsStatusFilterSignal.peek();
      const idx = PARTS_FILTER_TAB_ORDER.indexOf(currentFilter);
      const prevIdx = idx >= 0 ? (idx - 1 + PARTS_FILTER_TAB_ORDER.length) % PARTS_FILTER_TAB_ORDER.length : PARTS_FILTER_TAB_ORDER.length - 1;
      const newFilter = PARTS_FILTER_TAB_ORDER[prevIdx];
      if (newFilter) switchPartsFilterTab(newFilter);
    } else if (e.key === 'l' || e.key === 'ArrowRight') {
      e.preventDefault();
      const currentFilter = partsStatusFilterSignal.peek();
      const idx = PARTS_FILTER_TAB_ORDER.indexOf(currentFilter);
      const nextIdx = idx >= 0 ? (idx + 1) % PARTS_FILTER_TAB_ORDER.length : 0;
      const newFilter = PARTS_FILTER_TAB_ORDER[nextIdx];
      if (newFilter) switchPartsFilterTab(newFilter);
    }
  };

  window.addEventListener('keydown', handleKeyDown);
  _partsKeyDownHandler = handleKeyDown;
}

let _partsEffectDisposer: (() => void) | null = null;

export function setupPartsApprovalsDashboardListeners(): void {
  if (_partsEffectDisposer) {
    _partsEffectDisposer();
    _partsEffectDisposer = null;
  }

  _partsEffectDisposer = effect(() => {
    partsStatusFilterSignal.value;
    approvalsLoadingSignal.value;
    recommendationsSignal.value;
    approvalsCurrentPageSignal.value;
    recommendationsInEditMappingMode.value;

    const slot = document.getElementById('main-content-slot');
    const route = window.location.hash;
    if (slot && route === '#parts-approvals') {
      untracked(() => {
        savePartsDashboardScrollState();
        slot.innerHTML = renderPartsApprovalsDashboard();
        bindFilterListeners();
        bindActionButtons();
        bindBatchButtons();
        bindSignatureButtons();
        bindPageListeners();
        bindRejectModalListeners();
        bindBatchModalListeners();
        bindKeyboardListeners();
        initializeInlineAutocompletes();
        bindEditMappingListeners();
        restorePartsDashboardScrollState();
      });
    }
  });

  bindFilterListeners();
  bindActionButtons();
  bindBatchButtons();
  bindSignatureButtons();
  bindPageListeners();
  bindRejectModalListeners();
  bindBatchModalListeners();
  bindKeyboardListeners();
  initializeInlineAutocompletes();
  bindEditMappingListeners();
  restorePartsDashboardScrollState();
}

export function unmountPartsApprovalsDashboard(): void {
  if (_partsEffectDisposer) {
    _partsEffectDisposer();
    _partsEffectDisposer = null;
  }
  if (_partsKeyDownHandler) {
    window.removeEventListener('keydown', _partsKeyDownHandler);
    _partsKeyDownHandler = null;
  }
}

function bindEditMappingListeners(): void {
  const editButtons = document.querySelectorAll('.btn-edit-mapping');
  editButtons.forEach((btn) => {
    btn.addEventListener('click', (e) => {
      const target = e.currentTarget as HTMLElement;
      const recId = target.dataset.recommendationId;
      if (recId) {
        const newSet = new Set(recommendationsInEditMappingMode.value);
        newSet.add(recId);
        recommendationsInEditMappingMode.value = newSet;
      }
    });
  });

  const cancelButtons = document.querySelectorAll('.btn-cancel-edit-mapping');
  cancelButtons.forEach((btn) => {
    btn.addEventListener('click', (e) => {
      const target = e.currentTarget as HTMLElement;
      const recId = target.dataset.recommendationId;
      if (recId) {
        const newSet = new Set(recommendationsInEditMappingMode.value);
        newSet.delete(recId);
        recommendationsInEditMappingMode.value = newSet;
        tempMappings.delete(recId);
      }
    });
  });
}

function initializeInlineAutocompletes(): void {
  const mappers = document.querySelectorAll('.inline-consumable-mapper');
  const items = (consumablesSignal.value || []).map((c: any) => ({
    id: c.id,
    label: `${c.vendor || ''} ${c.name} (${c.code || ''})`.trim()
  }));

  mappers.forEach((el: any) => {
    el.items = items;
    const selectedId = el.getAttribute('selected-id');
    if (selectedId) {
      el.selectedId = selectedId;
    }
    el.addEventListener('select', async (e: CustomEvent) => {
      el.selectedId = e.detail.id;
      const recId = el.dataset.recommendationId;
      if (!recId) return;

      const rec = (recommendationsSignal.value || []).find((r) => r.id === recId);
      const isAlreadyMapped = rec && rec.consumableId;

      if (isAlreadyMapped) {
        if (e.detail.id) {
          try {
            el.setAttribute('disabled', 'true');
            const res = await fetch(`/api/v1/parts-recommendations/${recId}`, {
              method: 'PATCH',
              headers: getAuthHeaders(),
              body: JSON.stringify({ consumableId: e.detail.id })
            });
            if (res.ok) {
              const updated = (await res.json()) as PartRecommendation;
              updateRecommendationList(updated);
              showToast('Consumable mapping updated successfully.', 'success');
              const newSet = new Set(recommendationsInEditMappingMode.value);
              newSet.delete(recId);
              recommendationsInEditMappingMode.value = newSet;
            } else {
              const errBody = await res.json().catch(() => ({ error: 'Update failed' }));
              showToast(errBody.error || 'Failed to update mapping.', 'error');
            }
          } catch (err) {
            console.error('Failed to update mapping:', err);
            showToast('Network error while updating mapping.', 'error');
          } finally {
            el.removeAttribute('disabled');
          }
        }
      } else {
        if (e.detail.id) {
          tempMappings.set(recId, e.detail.id);
        } else {
          tempMappings.delete(recId);
        }
        window.dispatchEvent(new CustomEvent('opm-state-change'));
      }
    });
  });
}
